/**
 * Node-style polyfills for browser environments  
 * Prevents Deno.core.runMicrotasks() errors from libraries expecting Node APIs
 * Chrome Extension MV3 compatible - pure browser globals only
 * BROWSER ONLY - Do not import in edge/server functions
 */

// Prevent any Deno APIs from being called
if (typeof globalThis !== 'undefined') {
  // Completely disable Deno if it somehow exists
  if ((globalThis as any).Deno) {
    console.warn('Deno detected in browser - disabling to prevent errors');
    (globalThis as any).Deno = {
      core: {
        runMicrotasks: () => Promise.resolve()
      }
    };
  }
  
  // Also check window object
  if (typeof window !== 'undefined' && (window as any).Deno) {
    console.warn('Deno detected on window - disabling');
    (window as any).Deno = undefined;
  }
}

// Polyfill for process.nextTick using queueMicrotask (pure browser API)
const nextTick = (callback: (...args: any[]) => void, ...args: any[]) => {
  if (typeof queueMicrotask !== 'undefined') {
    queueMicrotask(() => callback(...args));
  } else {
    Promise.resolve().then(() => callback(...args));
  }
};

// Polyfill for setImmediate using setTimeout (pure browser API)
const setImmediate = (callback: (...args: any[]) => void, ...args: any[]): number => {
  return setTimeout(() => callback(...args), 0) as any;
};

// Polyfill for clearImmediate using clearTimeout (pure browser API)
const clearImmediate = (id: number): void => {
  clearTimeout(id);
};

// Create a safe process object
const safeProcess = {
  nextTick,
  env: {}, // Keep empty - never leak server secrets to client
  browser: true,
  version: '',
  versions: { node: '' },
  platform: 'browser',
  arch: 'browser',
  title: 'browser',
  pid: 0,
  ppid: 0,
  argv: [],
  argv0: '',
  execArgv: [],
  execPath: '',
  cwd: () => '/',
  chdir: () => {},
  umask: () => 0,
  hrtime: {
    bigint: () => BigInt(Date.now() * 1000000)
  }
};

// Only inject if we're in a browser environment
if (typeof window !== 'undefined') {
  // Ensure process exists - don't override if already present
  if (!(window as any).process) {
    (window as any).process = safeProcess;
  }
  
  // Ensure global exists and points to window - don't override if already present
  if (!(window as any).global) {
    (window as any).global = window;
  }
  
  // Add setImmediate/clearImmediate if they don't exist
  if (!(window as any).setImmediate) {
    (window as any).setImmediate = setImmediate;
  }
  
  if (!(window as any).clearImmediate) {
    (window as any).clearImmediate = clearImmediate;
  }
  
  // Add empty Buffer polyfill to prevent errors - don't override if already present
  if (!(window as any).Buffer) {
    (window as any).Buffer = class Buffer {
      static from() { return new Uint8Array(); }
      static alloc() { return new Uint8Array(); }
      static allocUnsafe() { return new Uint8Array(); }
      static isBuffer() { return false; }
    };
  }
}

// Also set them on globalThis for broader compatibility
if (typeof globalThis !== 'undefined') {
  // Don't override existing globals
  if (!(globalThis as any).process) {
    (globalThis as any).process = safeProcess;
  }
  
  if (!(globalThis as any).global) {
    (globalThis as any).global = globalThis;
  }
  
  if (!(globalThis as any).setImmediate) {
    (globalThis as any).setImmediate = setImmediate;
  }
  
  if (!(globalThis as any).clearImmediate) {
    (globalThis as any).clearImmediate = clearImmediate;
  }
}

export { nextTick, setImmediate, clearImmediate, safeProcess };